import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmt, StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

export default function EventSheet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/bookings/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [id]);

  if (!data) return <div className="py-20 text-center text-sm text-muted-foreground">Preparing event sheet…</div>;

  const Section = ({ title, children }) => (
    <div className="mt-6 border border-border" data-testid={`sheet-section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="border-b border-border bg-muted/50 px-4 py-2 font-heading text-xs font-bold uppercase tracking-[0.2em]">{title}</div>
      <div className="p-4">{children}</div>
    </div>
  );

  return (
    <div data-testid="event-sheet-page" className="mx-auto max-w-3xl">
      <div className="mb-4 flex items-center justify-between print:hidden">
        <button data-testid="sheet-back-button" onClick={() => navigate(`/bookings/${id}`)} className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-ink">
          <ArrowLeft className="h-4 w-4" /> Back to booking
        </button>
        <Button data-testid="sheet-print-button" onClick={() => window.print()} className="rounded-md bg-ink hover:bg-ink-light">
          <Printer className="mr-2 h-4 w-4" /> Print Event Sheet
        </Button>
      </div>

      <div className="border border-border bg-white p-8">
        <div className="flex items-start justify-between border-b-2 border-ink pb-4">
          <div>
            <h1 className="font-heading text-2xl font-black tracking-tight">EVENT SHEET</h1>
            <p className="font-mono text-sm font-bold" data-testid="sheet-ref">{data.booking_ref}</p>
          </div>
          <div className="text-right text-sm">
            <p className="font-heading font-bold">Fleet &amp; Feast Catering</p>
            <p className="text-muted-foreground">1 Depot Lane, London E1 6AN</p>
            <StatusBadge status={data.status} />
          </div>
        </div>

        <Section title="Event Details">
          <dl className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Customer</dt><dd className="font-medium">{data.customer_name}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Date</dt><dd className="font-bold tabular-nums">{data.event_date}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Guests</dt><dd className="font-medium tabular-nums">{data.guest_count}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Menu</dt><dd className="font-medium">{data.menu_name || "—"}</dd></div>
          </dl>
        </Section>

        <Section title="Venue & Access">
          {data.venue ? (
            <div className="text-sm">
              <p className="font-medium">{data.venue.name}</p>
              <p className="text-muted-foreground">{data.venue.address}</p>
              {data.venue.lat != null && <p className="mt-1 font-mono text-xs">GPS {Number(data.venue.lat).toFixed(4)}, {Number(data.venue.lng).toFixed(4)}</p>}
              <p className="mt-2">Site contact: {data.venue.contact_name || "—"} · {data.venue.contact_phone || "—"}</p>
              <p>Power: {data.venue.power_available ? "Yes" : "No"} · Water: {data.venue.water_available ? "Yes" : "No"}{data.venue.max_vehicle_height ? ` · Max vehicle height ${data.venue.max_vehicle_height}m` : ""}</p>
              {(data.venue.site_rules || []).length > 0 && (
                <ul className="mt-2 list-inside list-disc font-medium text-amber-800">
                  {data.venue.site_rules.map((r) => <li key={r}>{r}</li>)}
                </ul>
              )}
            </div>
          ) : <p className="text-sm text-muted-foreground">Venue to be confirmed.</p>}
        </Section>

        <Section title="Mobile Units">
          <ul className="list-inside list-disc text-sm">
            {data.units.map((u) => <li key={u.id}>{u.name} — {u.capacity} covers ({u.unit_type})</li>)}
            {data.units.length === 0 && <li className="text-muted-foreground">No units assigned</li>}
          </ul>
        </Section>

        <Section title="Kitchen Production">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left"><th className="py-1">Item</th><th>Course</th><th>Portion</th><th className="text-right">Qty</th></tr></thead>
            <tbody>
              {(data.kitchen_tasks || []).map((t) => (
                <tr key={t.id} className="border-b border-border last:border-0">
                  <td className="py-1.5">{t.name}</td><td className="capitalize">{t.course}</td><td>{t.portion}</td><td className="text-right tabular-nums">× {t.quantity}</td>
                </tr>
              ))}
              {(data.kitchen_tasks || []).length === 0 && <tr><td colSpan={4} className="py-2 text-muted-foreground">Assign a menu to generate the production list.</td></tr>}
            </tbody>
          </table>
          </div>
        </Section>

        <Section title="Staff Rota">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left"><th className="py-1">Name</th><th>Role</th><th>Start</th><th>End</th></tr></thead>
            <tbody>
              {(data.staff_assignments || []).map((s) => (
                <tr key={s.id} className="border-b border-border last:border-0">
                  <td className="py-1.5 font-medium">{s.name}</td><td>{s.role}</td><td className="tabular-nums">{s.start_time || "—"}</td><td className="tabular-nums">{s.end_time || "—"}</td>
                </tr>
              ))}
              {(data.staff_assignments || []).length === 0 && <tr><td colSpan={4} className="py-2 text-muted-foreground">No staff assigned.</td></tr>}
            </tbody>
          </table>
          </div>
        </Section>

        <Section title="External Providers & Equipment">
          <ul className="list-inside list-disc text-sm">
            {data.providers.map((p) => <li key={p.id}>{p.name} — {p.category} ({fmt(p.rate)})</li>)}
            {(data.equipment_assignments || []).map((e) => <li key={e.id}>{e.name} × {e.quantity}</li>)}
            {data.providers.length === 0 && (data.equipment_assignments || []).length === 0 && <li className="text-muted-foreground">None assigned</li>}
          </ul>
        </Section>

        <Section title="Financials">
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Contract total</dt><dd className="font-bold tabular-nums">{fmt(data.quote?.total)}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">Paid to date</dt><dd className="tabular-nums">{fmt(data.amount_paid)}</dd></div>
            <div className="flex justify-between border-t border-border pt-1.5"><dt className="font-medium">Balance due</dt><dd className="font-bold tabular-nums">{fmt((data.quote?.total || 0) - (data.amount_paid || 0))}</dd></div>
          </dl>
        </Section>
      </div>
    </div>
  );
}
